import { useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { Text } from '@react-three/drei';
import { AdditiveBlending, Vector3 } from 'three';
import { AsteroidRing } from './AsteroidRing.jsx';
import { RingedPlanet } from './RingedPlanet.jsx';
import { softSpriteTexture } from './softSprite.js';
import { RING_RADIUS, RING_STOPS, tabletAngle } from '../journeyPath.js';
import { portfolioContent } from '../../content/portfolioContent.js';

const FONT = '/assets/fonts/space-grotesk/SpaceGrotesk-Regular.ttf';

const worldPos = new Vector3();
const toCamera = new Vector3();
const outward = new Vector3();

// One thin dark-glass tablet per content cluster (spec: each section gets its
// own object, clearly legible). Hover = the flat page's rocking-tile mechanic
// translated to 3D: exponential-spring tilt (±4.5°/±4°), a cursor-tracking
// glare highlight, and a slight lift toward the camera.
function buildTablets() {
  const c = portfolioContent.credentials;
  const clusters = [
    ...c.certGroups.map((g) => ({ label: g.label.toUpperCase(), text: g.items.join(' · ') })),
    ...c.skillGroups.map((g) => ({ label: g.label.toUpperCase(), text: g.items.join(' · ') })),
    { label: 'EDUCATION', text: c.education.join(' · ') },
    { label: 'TOOLS', text: c.tools.join(' · ') },
  ].slice(0, RING_STOPS);
  return clusters.map((cluster, i) => ({ ...cluster, index: i }));
}

function GlassTablet({ tablet }) {
  const rootRef = useRef();
  const rockRef = useRef();
  const glareRef = useRef();
  const spring = useRef({ tx: 0, ty: 0, tl: 0, go: 0, gx: 0, gy: 0 });
  const glow = softSpriteTexture({
    stops: [
      [0, 'rgba(255,196,107,0.35)'],
      [0.5, 'rgba(255,196,107,0.1)'],
      [1, 'rgba(255,196,107,0)'],
    ],
  });

  useFrame((state, dt) => {
    const st = spring.current;
    const node = rockRef.current;
    if (!node) return;
    // transparent glass would show mirrored back-side text — hide once the
    // tablet turns edge-on to the camera (it's a sliver there, so no pop)
    const root = rootRef.current;
    if (root) {
      root.getWorldPosition(worldPos);
      toCamera.copy(state.camera.position).sub(worldPos).normalize();
      root.getWorldDirection(outward); // group +Z after rotation = outward normal
      const facing = outward.dot(toCamera);
      const visible = facing > 0.03;
      if (root.visible !== visible) root.visible = visible;
      if (!visible) return;
    }
    const k = 1 - Math.exp(-9 * dt);
    node.rotation.x += (st.tx - node.rotation.x) * k;
    node.rotation.y += (st.ty - node.rotation.y) * k;
    node.position.z += (st.tl - node.position.z) * k;
    const glare = glareRef.current;
    if (glare) {
      glare.material.opacity += (st.go - glare.material.opacity) * k;
      glare.position.x += (st.gx - glare.position.x) * k;
      glare.position.y += (st.gy - glare.position.y) * k;
    }
  });

  function handleMove(event) {
    event.stopPropagation();
    const uv = event.uv;
    if (!uv) return;
    const st = spring.current;
    st.ty = (uv.x - 0.5) * (9 * Math.PI) / 180; // ±4.5° — flat-tile mechanic
    st.tx = -(0.5 - uv.y) * (8 * Math.PI) / 180; // ±4°, top leans away
    st.tl = 0.07;
    st.go = 0.55;
    st.gx = (uv.x - 0.5) * 1.5;
    st.gy = (uv.y - 0.5) * 0.86;
    document.body.style.cursor = 'pointer';
  }

  function handleOut() {
    const st = spring.current;
    st.tx = 0;
    st.ty = 0;
    st.tl = 0;
    st.go = 0;
    document.body.style.cursor = '';
  }

  const angle = tabletAngle(tablet.index);
  const x = Math.cos(angle) * RING_RADIUS;
  const z = Math.sin(angle) * RING_RADIUS;

  return (
    <group ref={rootRef} position={[x, 0.4 + (tablet.index % 2) * 0.14, z]} rotation={[0, -angle + Math.PI / 2, 0]}>
      <group ref={rockRef}>
        {/* thin flat dark glass */}
        <mesh onPointerMove={handleMove} onPointerOut={handleOut}>
          <boxGeometry args={[1.5, 0.86, 0.028]} />
          <meshStandardMaterial color="#0b0e16" transparent opacity={0.62} roughness={0.26} metalness={0.12} />
        </mesh>
        {/* cursor-tracking glare (3D analogue of .panel-glare) */}
        <sprite ref={glareRef} position={[0, 0, 0.05]} scale={[1.1, 0.7, 1]}>
          <spriteMaterial map={glow} transparent opacity={0} depthWrite={false} blending={AdditiveBlending} />
        </sprite>
        <mesh position={[0, 0.31, 0.02]}>
          <boxGeometry args={[1.34, 0.014, 0.006]} />
          <meshBasicMaterial color="#e8a33d" />
        </mesh>
        {/* sdfGlyphSize 128 (troika default 64): the tablets read at small
            on-screen glyph sizes during the flyover — the higher-res SDF is
            what keeps their edges crisp instead of mushy */}
        <Text
          position={[-0.66, 0.22, 0.025]}
          font={FONT}
          fontSize={0.085}
          color="#ffc46b"
          anchorX="left"
          anchorY="middle"
          letterSpacing={0.12}
          sdfGlyphSize={128}
        >
          {tablet.label}
        </Text>
        <Text
          position={[-0.66, -0.08, 0.025]}
          font={FONT}
          fontSize={0.062}
          lineHeight={1.35}
          color="#eceef2"
          anchorX="left"
          anchorY="middle"
          maxWidth={1.32}
          sdfGlyphSize={128}
        >
          {tablet.text}
        </Text>
      </group>
    </group>
  );
}

export function CredentialRing({ position = [0, 0, 0] }) {
  const tablets = useMemo(buildTablets, []);

  return (
    <group position={position}>
      <RingedPlanet radius={1.1} ringInner={1.35} ringOuter={1.95} ringTilt={0.5} />
      {/* asteroid band shares the ring plane tilt */}
      <AsteroidRing inner={1.6} outer={2.15} tilt={0.5} spin={0.02} scale={1.1} />
      {tablets.map((tablet) => (
        <GlassTablet key={tablet.label} tablet={tablet} />
      ))}
    </group>
  );
}
